import { useCallback, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import {
  publicAnnouncementApi,
  type CategoryFilter,
} from '@/api/publicAnnouncements'
import { AnnouncementCategoryFilter } from '@/components/home/announcements/AnnouncementCategoryFilter'
import { AnnouncementSearchBar } from '@/components/home/announcements/AnnouncementSearchBar'
import { AnnouncementTable } from '@/components/home/announcements/AnnouncementTable'
import { AnnouncementPagination } from '@/components/home/announcements/AnnouncementPagination'

const PAGE_SIZE = 20

export default function AnnouncementsPage() {
  const { t } = useTranslation('announcements')
  const navigate = useNavigate()

  const [page, setPage] = useState(0)
  const [category, setCategory] = useState<CategoryFilter>('all')
  const [searchInput, setSearchInput] = useState('')
  const [appliedKeyword, setAppliedKeyword] = useState('')

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['public-announcements', page, category, appliedKeyword],
    queryFn: () =>
      publicAnnouncementApi.getList({
        page,
        size: PAGE_SIZE,
        category,
        keyword: appliedKeyword || undefined,
      }),
  })

  const handleSearch = useCallback(() => {
    setAppliedKeyword(searchInput.trim())
    setPage(0)
  }, [searchInput])

  const handleCategoryChange = useCallback((next: CategoryFilter) => {
    setCategory(next)
    setPage(0)
  }, [])

  const rows = data?.announcements ?? []
  const totalPages = data?.totalPages ?? 0

  return (
    <section className="max-w-[1136px] mx-auto pt-4 pb-20 lg:pt-16 lg:pb-[120px]">
      <h1 className="px-6 lg:px-0 mb-10 lg:mb-20 text-center text-2xl lg:text-5xl font-bold text-[#131313] tracking-[-0.6px] leading-[1.4] lg:leading-[1.2]">
        {t('title')}
      </h1>

      <div className="flex flex-col-reverse gap-6 px-6 mb-6 lg:flex-row lg:items-center lg:justify-between lg:gap-4 lg:px-0">
        <AnnouncementCategoryFilter
          value={category}
          onChange={handleCategoryChange}
        />
        <AnnouncementSearchBar
          searchInput={searchInput}
          onSearchInputChange={setSearchInput}
          onSubmit={handleSearch}
        />
      </div>

      {isLoading && (
        <div role="status" aria-live="polite" className="py-20 text-center text-[#a0a0a0]">
          {t('detail.loading')}
        </div>
      )}

      {!isLoading && isError && (
        <div role="alert" className="flex flex-col items-center gap-4 py-20 px-6 text-center">
          <p className="text-[#131313]">{t('errorTitle')}</p>
          <button
            type="button"
            onClick={() => refetch()}
            className="px-5 h-10 rounded-full bg-[#4599ff] text-white text-sm font-medium hover:brightness-95"
          >
            {t('retry')}
          </button>
        </div>
      )}

      {!isLoading && !isError && rows.length === 0 && (
        <div className="py-20 text-center text-[#a0a0a0]">{t('empty')}</div>
      )}

      {!isLoading && !isError && rows.length > 0 && (
        <>
          <AnnouncementTable
            rows={rows}
            onRowClick={(id) => navigate(`/announcements/${id}`)}
          />
          <AnnouncementPagination
            page={page}
            totalPages={totalPages}
            onChange={setPage}
          />
        </>
      )}
    </section>
  )
}
